<script setup>
import { computed, ref } from 'vue';
import { useMessageContext } from '../../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS } from '../../constants';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const { variant, isPrivate } = useMessageContext();
const currentAccountId = useMapGetter('getCurrentAccountId');

const showGallery = ref(false);
const galleryAttachment = ref(null);

const conversationLink = displayId => {
  if (!displayId || !currentAccountId.value) return null;
  const path = conversationUrl({
    accountId: currentAccountId.value,
    id: displayId,
  });
  return frontendURL(path);
};

// Same behavior as InternalChat: turn "#12345" into a markdown link to that conversation
const addConversationMentionLinks = content => {
  if (!content || !currentAccountId.value) return content || '';
  const mentionRegex = /(^|[\s>])#(\d+)\b/g;
  return content.replace(mentionRegex, (match, prefix, id) => {
    const url = conversationLink(id);
    if (!url) return match;
    return `${prefix}[#${id}](${url})`;
  });
};

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.content;
  }

  const content = isPrivate.value
    ? addConversationMentionLinks(props.content)
    : props.content;

  return new MessageFormatter(content).formattedMessage;
});

const proseBubbleModifier = computed(() => {
  if (
    variant.value === MESSAGE_VARIANTS.USER ||
    variant.value === MESSAGE_VARIANTS.AGENT
  ) {
    return 'prose-bubble-light';
  }
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'prose-bubble [&_a.link]:underline [&_a.link]:font-medium';
  }
  return 'prose-bubble';
});

const handleContentClick = event => {
  const isImageElement = event.target?.tagName === 'IMG';
  const isWrappedInLink = event.target?.closest('A');
  if (!isImageElement || isWrappedInLink) return;

  galleryAttachment.value = {
    file_type: 'image',
    data_url: event.target.src,
    message_id: Date.now(),
  };
  showGallery.value = true;
};
</script>

<template>
  <div>
    <span
      v-dompurify-html="formattedContent"
      class="prose"
      :class="proseBubbleModifier"
      @click="handleContentClick"
    />
    <GalleryView
      v-if="showGallery"
      v-model:show="showGallery"
      :attachment="galleryAttachment"
      :all-attachments="[galleryAttachment]"
      @close="showGallery = false"
      @error="showGallery = false"
    />
  </div>
</template>
